import { useFormikContext } from "formik";
import { motion } from "framer-motion";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import __ from "web/Layout/Translations";

import ArrowUpIcon from "web/assets/icons/arrow-up.svg";

import useDropdown from "web/hooks/useDropdown";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import {
  getNextSelectIndex,
  isSelectCloseKey,
  isSelectCommitKey,
  isSelectNavKey,
  isSelectOpenKey,
} from "web/utils/ui/selectKeyboard";

import Dropdown from "./Dropdown";
import { DISTANCE_DROPDOWN_ID } from "./const";
import classes from "./distance.scss";

interface IDistanceProps {
  name: string;
  options: {
    value: string;
    label: string;
  }[];
}

const dropdownVariants = {
  open: {
    height: "auto",
  },
  closed: {
    height: 0,
  },
};
const transition = { duration: 0.25 };

const Distance: FC<IDistanceProps> = ({ name, options }) => {
  const { values, setFieldValue } = useFormikContext<Record<string, string>>();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useDropdown({
    scopeSelector: `.${classes.root}`,
  });
  const valueCurrent = values && values[name];
  const optionCurrent = valueCurrent
    ? options.find((optionItem) => optionItem.value === valueCurrent)
    : null;
  const labelCurrent = optionCurrent ? optionCurrent.label : null;
  const classNameWrapper = isOpen ? classes.wrapperActive : classes.wrapper;

  const selectedIndex = useMemo(() => {
    const idx = options?.findIndex((o) => o.value === valueCurrent);
    return idx >= 0 ? idx : 0;
  }, [options, valueCurrent]);

  const [activeIndex, setActiveIndex] = useState<number>(selectedIndex);

  useEffect(() => {
    if (!isOpen) return;
    setActiveIndex(selectedIndex);
  }, [isOpen, selectedIndex]);

  const changeHandler = useCallback(
    (valueChosen: string) => {
      setFieldValue(name, valueChosen);
      setIsOpen((status) => !status);
    },
    [setIsOpen, setFieldValue, name]
  );

  return (
    <div className={classes.root}>
      <div className={classNameWrapper}>
        <button
          type="button"
          className={classes.trigger}
          onClick={() => setIsOpen((state) => !state)}
          onKeyDown={(e) => {
            if (!isArrayHasItems(options)) return;
            const { key } = e;

            if (!isOpen && isSelectOpenKey(key)) {
              e.preventDefault();
              setIsOpen(true);
              setActiveIndex(selectedIndex);
              return;
            }

            if (!isOpen) return;

            const currentIndex =
              activeIndex >= 0 && activeIndex < options.length
                ? activeIndex
                : selectedIndex;

            if (isSelectNavKey(key)) {
              e.preventDefault();
              const nextIndex =
                getNextSelectIndex({
                  key,
                  currentIndex,
                  count: options.length,
                }) ?? currentIndex;
              setActiveIndex(nextIndex);
              return;
            }

            if (isSelectCommitKey(key)) {
              e.preventDefault();
              const chosen = options[activeIndex]?.value ?? valueCurrent;
              if (chosen) changeHandler(chosen);
              setIsOpen(false);
              triggerRef.current?.focus();
              return;
            }

            if (isSelectCloseKey(key)) {
              e.preventDefault();
              setIsOpen(false);
              triggerRef.current?.focus();
            }
          }}
          data-t1="distance_trigger_button"
          aria-label={`${__("Otwórz listę odległości")}. ${__(
            "Obecnie wybrana opcja"
          )}: ${labelCurrent}`}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={DISTANCE_DROPDOWN_ID}
          role="combobox"
          ref={triggerRef}
        >
          {labelCurrent} <ArrowUpIcon width={10} className={classes.arrow} />
        </button>
        <div className={classes.body} data-t1="distance_trigger_dropdown">
          <motion.div
            className={classes.dropdown}
            animate={isOpen ? "open" : "closed"}
            transition={transition}
            variants={dropdownVariants}
          >
            <Dropdown
              options={options}
              onChange={changeHandler}
              value={valueCurrent}
              isOpen={isOpen}
              activeIndex={activeIndex}
              onRequestActiveIndexChange={(nextIndex: number) => {
                const bounded = Math.max(
                  0,
                  Math.min(nextIndex, options.length - 1)
                );
                setActiveIndex(bounded);
              }}
              onRequestSelect={(index: number) => {
                const option = options[index];
                if (!option) return;
                changeHandler(option.value);
                setIsOpen(false);
                triggerRef.current?.focus();
              }}
              onRequestClose={() => {
                setIsOpen(false);
                triggerRef.current?.focus();
              }}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Distance;
